import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { useDashboardSubscription } from "./useDashboardSubscription";
import { useDashboardActions } from "./useDashboardActions";
import { usePublicationLimits } from "./usePublicationLimits";
import { QueueCard } from "./QueueCard";
import { CounterCard } from "./CounterCard";
import { PublicationLimitsCard } from "./PublicationLimitsCard";
import { IfPermission } from "../../auth/IfPermission";
import type { JSX } from "react";

export function Dashboard() {
  let queueSection: JSX.Element;
  const { state } = useDashboardSubscription();
  const {
    generationActionState,
    publicationActionState,
    pauseGeneration,
    resumeGeneration,
    pausePublication,
    resumePublication,
  } = useDashboardActions();
  const { state: limitsState, save: saveLimits, retry: retryLimits } = usePublicationLimits();

  if (state.kind === "loading") {
    queueSection = (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <CircularProgress />
      </Box>
    );
  } else {
    if (state.kind === "error") {
      queueSection = <Alert severity="error">Ошибка подключения к дашборду. Переподключение…</Alert>;
    } else {
      queueSection = (
        <>
          <Typography variant="h4" mb={3}>
            Панель управления
          </Typography>

          <Box display="flex" flexWrap="wrap" gap={3}>
            <IfPermission permission="generation.queue.view">
              <QueueCard
                title="Генерация"
                queueState={state.data.generation}
                actionState={generationActionState}
                onPause={pauseGeneration}
                onResume={resumeGeneration}
                controlPermission="generation.queue.control"
                pauseDescription="Все ожидающие задачи генерации будут удалены из очереди. Текущие процессы завершат работу."
                resumeDescription="Все анкоры со статусом Queued будут поставлены в очередь на генерацию."
              />
            </IfPermission>
            <IfPermission permission="publications.queue.view">
              <QueueCard
                title="Публикация"
                queueState={state.data.publication}
                sitewideQueuedCount={state.data.publication.sitewideQueuedCount}
                actionState={publicationActionState}
                onPause={pausePublication}
                onResume={resumePublication}
                controlPermission="publications.queue.control"
                pauseDescription="Все ожидающие задачи публикации будут удалены из очереди. Текущие операции завершат работу."
                resumeDescription="Все ожидающие элементы активных и запланированных публикаций будут поставлены в очередь (отложенные — с сохранением расписания)."
              />
            </IfPermission>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={3} mt={3}>
            <CounterCard title="Проверка публикаций" counterState={state.data.publicationCheck} />
            <CounterCard title="Проверка сайтов" counterState={state.data.siteVerification} />
            <CounterCard
              title="Очередь наполнения проверки доступов"
              counterState={state.data.siteVerificationFanout}
            />
            <CounterCard title="Проверка прокси" counterState={state.data.proxyVerification} />
            <CounterCard title="Сбор параметров сайтов" counterState={state.data.siteParamsCollection} />
            <CounterCard title="Очередь индексации" counterState={state.data.indexing} />
            <CounterCard
              title="Очередь сканирования ссылок конкурентов"
              counterState={state.data.competitorLinkScan}
            />
            <CounterCard
              title="Очередь удаления ссылок конкурентов"
              counterState={state.data.competitorLinkRemoval}
            />
          </Box>
        </>
      );
    }
  }

  // The limits section is a parallel automaton independent of the SSE
  // subscription (docs/screens/Dashboard.md): it renders below the queue
  // area in every SSE state, including loading and error.
  return (
    <Box p={3}>
      {queueSection}
      <Box mt={3}>
        <PublicationLimitsCard state={limitsState} onSave={saveLimits} onRetry={retryLimits} />
      </Box>
    </Box>
  );
}
